import { useState } from 'react';
import { Link } from 'react-router-dom';
import { autenticarUsuario, salvarSessao, obterSessao, limparSessao } from '../services/usuarios';
import './PortalCliente.css';

const sistemas = [
  {
    id: 'emissao-nfe',
    titulo: 'Emissão NFe',
    descricao: 'Automação e acompanhamento da emissão de notas fiscais eletrônicas.',
    status: 'Ativo',
  },
  {
    id: 'captura-nfe-sefaz',
    titulo: 'Captura de NFe no SEFAZ',
    descricao: 'Robô que captura automaticamente as notas fiscais emitidas contra o CNPJ da empresa.',
    status: 'Ativo',
  },
  {
    id: 'processamento-curriculos',
    titulo: 'Processamento de Currículos',
    descricao: 'Leitura de e-mails, captura de currículos anexados, atualização no sistema e planilha Excel de controle.',
    status: 'Ativo',
    atividades: [
      'Leitura e análise dos e-mails recebidos.',
      'Captura e processamento dos currículos anexados.',
      'Atualização das informações no sistema.',
      'Geração e atualização de planilha em Excel para controle e acompanhamento.',
    ],
  },
  {
    id: 'cargos-salarios',
    titulo: 'Sistema de Cargos e Salários',
    descricao: 'Desenvolvimento e automação da gestão de cargos e salários, com planilhas Excel de controle e análise.',
    status: 'Ativo',
    atividades: [
      'Desenvolvimento e implementação do sistema de Cargos e Salários.',
      'Automação completa dos processos relacionados ao cadastro, atualização e gestão de cargos e salários.',
      'Integração e geração de planilhas em Excel para controle, análise e acompanhamento das informações.',
    ],
  },
];

function gerarCaptcha() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let codigo = '';
  for (let i = 0; i < 5; i += 1) {
    codigo += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return { pergunta: codigo, resposta: codigo };
}

function PortalCliente() {
  const sessaoAtual = obterSessao();
  const [form, setForm] = useState({ email: '', senha: '', captcha: '' });
  const [captcha, setCaptcha] = useState(() => gerarCaptcha());
  const [status, setStatus] = useState(sessaoAtual?.token ? 'success' : 'idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [usuarioLogado, setUsuarioLogado] = useState(sessaoAtual?.email || '');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const renovarCaptcha = () => {
    setCaptcha(gerarCaptcha());
    setForm((prev) => ({ ...prev, captcha: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (form.captcha.trim().toUpperCase() !== captcha.resposta) {
      setStatus('error');
      setErrorMessage('Captcha incorreto. Tente novamente.');
      renovarCaptcha();
      return;
    }

    setStatus('submitting');

    try {
      const data = await autenticarUsuario({
        email: form.email.trim(),
        senha: form.senha,
      });

      const email = data?.email || form.email.trim();
      const token = data?.token;

      if (!token) {
        throw new Error('Resposta da API sem token.');
      }

      salvarSessao({ email, token });
      setUsuarioLogado(email);
      setForm({ email: '', senha: '', captcha: '' });
      setCaptcha(gerarCaptcha());
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMessage(err.message || 'E-mail ou senha inválidos.');
      renovarCaptcha();
    }
  };

  const handleLogout = () => {
    limparSessao();
    setUsuarioLogado('');
    setStatus('idle');
    renovarCaptcha();
  };

  return (
    <div className="portal-page">
      <div className="page-hero">
        <h1>Portal do Cliente</h1>
        <p>
          {status === 'success'
            ? 'Selecione um sistema para utilizar.'
            : 'Acesse seus sistemas e acompanhamento de automações.'}
        </p>
      </div>

      <section className="section">
        <div className="container">
          {status === 'success' ? (
            <div className="portal-dashboard">
              <div className="portal-dashboard-header">
                <div>
                  <h2>Meus sistemas</h2>
                  <p>Conectado como <strong>{usuarioLogado}</strong></p>
                </div>
                <button type="button" className="btn btn-outline" onClick={handleLogout}>
                  Sair
                </button>
              </div>

              <div className="portal-projetos-grid">
                {sistemas.map((sistema) => (
                  <article key={sistema.id} className="portal-projeto-card">
                    <span className="portal-projeto-status">{sistema.status}</span>
                    <h3>{sistema.titulo}</h3>
                    <p>{sistema.descricao}</p>
                    <Link to={`/portal/sistemas/${sistema.id}`} className="portal-projeto-cta">
                      Acessar sistema →
                    </Link>
                  </article>
                ))}
              </div>
            </div>
          ) : (
            <div className="portal-login-box">
              <h2>Entrar</h2>
              <p className="portal-subtitle">Use o e-mail e a senha cadastrados.</p>

              <form className="portal-login-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="portal-email">E-mail</label>
                  <input
                    type="email"
                    id="portal-email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    autoComplete="email"
                    placeholder="seu@email.com"
                    disabled={status === 'submitting'}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="portal-senha">Senha</label>
                  <div className="portal-password-wrap">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="portal-senha"
                      name="senha"
                      value={form.senha}
                      onChange={handleChange}
                      required
                      autoComplete="current-password"
                      placeholder="Sua senha"
                      disabled={status === 'submitting'}
                    />
                    <button
                      type="button"
                      className="portal-toggle-password"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    >
                      {showPassword ? 'Ocultar' : 'Mostrar'}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="portal-captcha">Captcha</label>
                  <div className="portal-captcha-row">
                    <div className="portal-captcha-code" aria-hidden="true">
                      {captcha.pergunta}
                    </div>
                    <button
                      type="button"
                      className="portal-captcha-refresh"
                      onClick={renovarCaptcha}
                      disabled={status === 'submitting'}
                      aria-label="Gerar novo captcha"
                    >
                      Trocar
                    </button>
                    <input
                      type="text"
                      id="portal-captcha"
                      name="captcha"
                      value={form.captcha}
                      onChange={handleChange}
                      required
                      autoComplete="off"
                      placeholder="Digite o código"
                      disabled={status === 'submitting'}
                      maxLength={5}
                    />
                  </div>
                </div>

                {status === 'error' && (
                  <p className="form-error" role="alert">{errorMessage}</p>
                )}

                <button type="submit" className="btn btn-primary btn-large" disabled={status === 'submitting'}>
                  {status === 'submitting' ? 'Entrando...' : 'Entrar'}
                </button>
              </form>

              <p className="portal-forgot">
                <Link to="/portal/esqueci-senha">Esqueci a senha</Link>
              </p>

              <p className="portal-switch">
                Ainda não tem cadastro?{' '}
                <Link to="/portal/cadastro">Criar conta</Link>
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default PortalCliente;
